import { buttonVariants } from "@car/ui/components/button";
import { cn } from "@car/ui/lib/utils";
import { ArrowLeft02Icon, Location01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link } from "@tanstack/react-router";
import { useRef, type RefObject } from "react";
import { z } from "zod";

import { CarGlyph } from "@/components/loader";
import { useSiteContent } from "@/lib/content";
import { faPlainNumber } from "@/lib/format";
import { EASE_OUT, ScrollTrigger, gsap, useGSAP } from "@/lib/gsap";
import { SETS, frameUrl, pickSet, type FrameSet } from "@/lib/hero-frames";

const cta =
  "h-12 rounded-xl px-6 text-base font-bold focus-visible:border-brand-ice focus-visible:ring-brand-ice/40";

/**
 * خط کند (۳G یا کندتر) یا حالت صرفه‌جویی داده، طبق Network Information API (Chrome؛ جای دیگر
 * undefined و همان مسیر کامل). مجموعه‌ی گوشی هم ~۴٫۵ مگابایت است: روی ۳G کند بیش از یک دقیقه.
 */
const connectionSchema = z.object({
  connection: z
    .object({ effectiveType: z.string().optional(), saveData: z.boolean().optional() })
    .optional(),
});
export const isThinLink = () => {
  const link = connectionSchema.safeParse(navigator).data?.connection;
  return link?.saveData === true || ["slow-2g", "2g", "3g"].includes(link?.effectiveType ?? "");
};
const clamp01 = (t: number) => Math.min(1, Math.max(0, t));
const lerp = (from: number, to: number, t: number) => from + (to - from) * t;
const smoothstep = (from: number, to: number, t: number) => {
  const x = clamp01((t - from) / (to - from));
  return x * x * (3 - 2 * x);
};

/**
 * دوربین: جای فریم روی بوم در پیشرفت f (۰ پوشیده، ۱ کامل).
 * افقی: کاور، حداکثر ۱.۲ برابر عرض تا دماغه و عقب خودرو بریده نشود، با کمی عقب‌کشیدن.
 * عمودی (گوشی): نمای نزدیک از چراغ جلو، دنبال کردن لبه‌ی پارچه تا در عقب، و در آخر عقب‌کشیدن تا کل خودرو
 * در عرض صفحه بنشیند؛ مرکز خودرو اول زیر متن است و در پایان میان تیتر و دکمه. still (کاهش حرکت): خودروی
 * کامل زیر متن اول، چون تیتر پایانی نمی‌آید.
 */
function camera(f: number, w: number, h: number, iw: number, ih: number, still = false) {
  if (w >= h) {
    const scale = Math.min(Math.max(w / iw, h / ih), (w * 1.2) / iw) * (1.08 - 0.08 * f);
    const dw = iw * scale;
    const dh = ih * scale;
    return { x: (w - dw) / 2, y: dh >= h ? (h - dh) / 2 : h * 0.62 - dh / 2, dw, dh };
  }
  const pull = smoothstep(0.58, 1, f);
  // در قاب آخر دماغه و عقب خودرو در ۰.۰۶۵ و ۰.۹۳۵ عرض فریم‌اند؛ ۱.۱۵ برابر عرض هر دو را نگه می‌دارد
  const far = (w * 1.15) / iw;
  const scale = lerp(Math.max((h * 0.6) / ih, far), far, pull);
  const dw = iw * scale;
  const dh = ih * scale;
  // کانون افقی در فریم: چراغ جلو (۰.۴۲) ← لبه‌ی پارچه روی در عقب (۰.۶) ← وسط خودرو
  const focus = lerp(lerp(0.42, 0.6, smoothstep(0.15, 0.65, f)), 0.5, pull);
  const x = Math.min(0, Math.max(w - dw, w / 2 - focus * dw));
  // مرکز خودرو در فریم حدود ۰.۵۸ ارتفاع است
  const y = (still ? 0.72 : lerp(0.7, 0.46, pull)) * h - 0.58 * dh;
  return { x, y, dw, dh };
}

/** شناسه‌ی ScrollTrigger پینِ هیرو (در home-motion ساخته می‌شود) */
export const HERO_TRIGGER = "hero-reveal";
/** رویدادی که home-motion با هر تغییر پیشرفت اسکرول روی خودِ <section> هیرو می‌فرستد؛ پیشرفت از خودِ تریگر خوانده می‌شود */
export const HERO_PROGRESS = "hero:progress";
/** پرده تا این بخش از پین کنار رفته است؛ باقی پین برای تیتر پایانی روی خودروی کامل است */
const FRAMES_END = 0.82;
/**
 * اگر شبکه کند باشد، صفحه پس از این مدت با هر چه رسیده باز می‌شود و بقیه پشت صحنه می‌آیند
 * (روی ۳G کند هر فریم گوشی ~۴۵ کیلوبایت است؛ انتظار برای همه‌ی ۹۶ فریم بیش از یک دقیقه می‌شد)
 */
const MAX_WAIT_MS = 6_000;
/** پیش‌بارگذار با رسیدن همین تعداد فریم اول کنار می‌رود؛ فریم‌های بعدی تا رسیدن اسکرول به آن‌ها می‌آیند */
const READY_FRAMES = 12;

/** رنگ صحنه (oklch(0.17 0.006 308)) به sRGB، برای بوم */
const STAGE = "16, 14, 18";

export type HeroNote = { title: string; href: string };

/**
 * هیرو «پرده‌برداری»: خودرو زیر پارچه‌ی بنفش برند است؛ اسکرول پرده را کنار می‌زند و چراغ‌ها را روشن
 * می‌کند و اسکرول به بالا دوباره رویش می‌کشد. بوم تمام‌صفحه، پین و نگاشت اسکرول در home-motion.
 */
export function Hero({ note }: { note?: HeroNote }) {
  const ref = useRef<HTMLElement>(null);
  const { hero } = useSiteContent();
  useUnveil(ref);

  return (
    <section ref={ref} className="hero" aria-labelledby="hero-title">
      <div className="hero-stage" aria-hidden="true">
        <canvas className="hero-canvas" data-canvas />
        <div className="hero-scrim" />
      </div>

      <div className="hero-content mx-auto w-full max-w-[1400px] px-4 sm:px-6">
        <div className="hero-copy">
          {note ? (
            <a href={note.href || "#"} className="hero-note">
              {note.title}
              <HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={1.8} className="size-4" />
            </a>
          ) : null}
          <h1 id="hero-title" className="hero-title">
            <span className="hero-line">{hero.titleTop}</span>
            <span className="hero-line">{hero.titleBottom}</span>
          </h1>
          <p className="hero-lede">{hero.lede}</p>
          <div className="hero-actions">
            <Link
              to="/vehicles"
              className={cn(
                buttonVariants({ size: "lg" }),
                cta,
                "bg-brand-yellow text-brand-charcoal hover:bg-brand-yellow/90",
              )}
            >
              {hero.primaryCta}
              <HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={1.8} className="size-5" />
            </Link>
            <a
              href="/#consult"
              className={cn(
                buttonVariants({ size: "lg" }),
                cta,
                "border-brand-ice/30 bg-[oklch(0.17_0.006_308_/_0.55)] text-brand-ice backdrop-blur-sm hover:bg-[oklch(0.22_0.006_308_/_0.7)]",
              )}
            >
              {hero.secondaryCta}
            </a>
          </div>
        </div>
      </div>

      {/* پس از کنار رفتن پرده؛ تا آن لحظه پنهان (visibility) تا با Tab هم دیده نشود.
          روی گوشی تیتر بالای خودرو و متن و دکمه زیر آن، در دسترس شست */}
      <div className="hero-reveal mx-auto w-full max-w-[1400px] px-4 sm:px-6">
        <p className="hero-reveal-title">{hero.revealTitle}</p>
        <div className="hero-reveal-foot">
          <p className="hero-reveal-text">{hero.revealText}</p>
          <Link
            to="/vehicles"
            className={cn(
              buttonVariants({ size: "lg" }),
              cta,
              "bg-brand-yellow text-brand-charcoal hover:bg-brand-yellow/90",
            )}
          >
            {hero.primaryCta}
            <HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={1.8} className="size-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/**
 * پیش‌بارگذار: از اولین رنگ (SSR) تا وقتی همه‌ی فریم‌ها دیکد شوند. بیرون از smooth-wrapper رندر می‌شود
 * چون محتوای ScrollSmoother ترنسفورم دارد و fixed را می‌شکند. بدون JS (noscript) یا با کاهش حرکت دیده
 * نمی‌شود، و اگر JS هرگز نرسد، CSS پس از ۲۰ ثانیه کنارش می‌زند. نوار پیشرفت یک جاده است: خودرو با
 * پیشرفت بارگذاری (‎--p روی پیش‌بارگذار) از شروع خواندن به نشانِ مقصد می‌رود.
 */
export function HeroPreloader() {
  return (
    <div
      className="preloader"
      data-preloader
      role="progressbar"
      aria-label="آماده‌سازی صفحه"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={0}
    >
      <noscript>
        <style>{".preloader{display:none}"}</style>
      </noscript>
      <img className="preloader-logo" src="/logo.webp" alt="" width={320} height={227} />
      <span className="preloader-count" data-count aria-hidden="true">
        ۰
      </span>
      <span className="preloader-road" aria-hidden="true">
        <span className="preloader-lane">
          <span className="preloader-car">
            <CarGlyph />
          </span>
        </span>
        <HugeiconsIcon icon={Location01Icon} strokeWidth={1.6} className="preloader-goal" />
      </span>
    </div>
  );
}

/** تا بیکار شدن ریسه‌ی اصلی صبر می‌کند، و دست‌بالا ۲۰۰ میلی‌ثانیه (سافاری requestIdleCallback ندارد) */
const whenIdle = () =>
  new Promise<void>((resolve) => {
    if ("requestIdleCallback" in window) requestIdleCallback(() => resolve(), { timeout: 200 });
    else setTimeout(resolve, 0);
  });

/**
 * بارگذاری به ترتیب با چند رشته‌ی هم‌زمان تا فریم‌های اول زودتر برسند؛ فریم خراب جای خالی می‌ماند.
 * فریم‌هایی که پیش‌بارگذار منتظرشان است با شش رشته و بی‌وقفه می‌آیند؛ بقیه با سه رشته و با
 * نوبت‌دادن به ریسه‌ی اصلی، چون دیکدِ ~۹۰ فریم درست روی هیدریشن و اولین لمس کاربر می‌افتاد.
 */
function loadFrames(
  set: FrameSet,
  frames: (HTMLImageElement | undefined)[],
  onProgress: (done: number) => void,
  signal: AbortSignal,
) {
  let next = 0;
  let done = 0;
  const one = async (index: number) => {
    const img = new Image();
    img.decoding = "async";
    img.src = frameUrl(set, index);
    try {
      await img.decode();
      frames[index] = img;
    } catch {
      // فریمِ نرسیده: نزدیک‌ترین فریمِ رسیده جایش کشیده می‌شود
    }
    if (!signal.aborted) onProgress(++done);
  };
  const run = (workers: number, until: number, yielding: boolean) =>
    Promise.all(
      Array.from({ length: workers }, async () => {
        while (next < until && !signal.aborted) {
          await one(next++);
          if (yielding) await whenIdle();
        }
      }),
    );
  const gate = Math.min(READY_FRAMES, frames.length);
  return run(6, gate, false).then(() => run(3, frames.length, true));
}

/** نزدیک‌ترین فریمِ بارشده به index (تا وقتی همه نرسیده‌اند) */
function nearest(frames: (HTMLImageElement | undefined)[], index: number) {
  for (let step = 0; step < frames.length; step++) {
    const img = frames[index - step] ?? frames[index + step];
    if (img) return img;
  }
  return undefined;
}

/**
 * پرده‌برداری: پیشرفت اسکرول (رویداد HERO_PROGRESS) هدف است و فریمِ نمایش‌داده هر rAF با میرایی نمایی
 * به آن نزدیک می‌شود، نه یک تصویر به ازای هر رویداد اسکرول. حلقه فقط تا رسیدن به هدف می‌چرخد.
 * کاهش حرکت: بی‌پیش‌بارگذار و بی‌اسکراب، فقط فریم آخر (خودروی کامل).
 */
function useUnveil(ref: RefObject<HTMLElement | null>) {
  useGSAP(
    () => {
      const root = ref.current;
      const canvas = root?.querySelector<HTMLCanvasElement>("[data-canvas]");
      const ctx = canvas?.getContext("2d", { alpha: false });
      if (!root || !canvas || !ctx) return;
      const preloader = document.querySelector<HTMLElement>("[data-preloader]");
      // کاهش حرکت، یا خط کند / «صرفه‌جویی داده»: فقط فریم آخر (خودروی کامل)، بی پیش‌بارگذار و اسکراب
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches || isThinLink();
      const set = pickSet();
      const frames: (HTMLImageElement | undefined)[] = Array.from({ length: SETS[set] });
      const last = frames.length - 1;
      const abort = new AbortController();

      let width = 0;
      let height = 0;
      // پیشرفت پرده‌برداری، ۰ تا ۱ (مستقل از تعداد فریم‌های مجموعه)
      let current = reduce ? 1 : 0;
      let target = current;
      let drawn = -1;

      const draw = (f: number) => {
        const img = nearest(frames, Math.round(f * last));
        if (!img || !width) return;
        const { x, y, dw, dh } = camera(
          f,
          width,
          height,
          img.naturalWidth,
          img.naturalHeight,
          reduce,
        );
        ctx.fillStyle = `rgb(${STAGE})`;
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, x, y, dw, dh);
        if (dh < height) {
          const band = dh * 0.22;
          const top = ctx.createLinearGradient(0, y, 0, y + band);
          top.addColorStop(0, `rgb(${STAGE})`);
          top.addColorStop(1, `rgba(${STAGE}, 0)`);
          ctx.fillStyle = top;
          ctx.fillRect(0, y - 1, width, band + 1);
          const bottom = ctx.createLinearGradient(0, y + dh - band, 0, y + dh);
          bottom.addColorStop(0, `rgba(${STAGE}, 0)`);
          bottom.addColorStop(1, `rgb(${STAGE})`);
          ctx.fillStyle = bottom;
          ctx.fillRect(0, y + dh - band, width, band + 1);
        }
        // چراغ‌ها: نمایشگاه تاریک شروع می‌شود و تا نیمه‌ی پرده‌برداری کاملاً روشن است
        const dark = 0.5 * (1 - Math.min(1, f * 2)) ** 2;
        if (dark > 0.002) {
          ctx.fillStyle = `rgba(${STAGE}, ${dark.toFixed(3)})`;
          ctx.fillRect(0, 0, width, height);
        }
        drawn = f;
      };

      const resize = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        width = Math.round(canvas.clientWidth * dpr);
        height = Math.round(canvas.clientHeight * dpr);
        canvas.width = width;
        canvas.height = height;
        ctx.imageSmoothingQuality = "high";
        draw(current);
      };
      const observer = new ResizeObserver(resize);
      observer.observe(canvas);

      if (reduce) {
        const img = new Image();
        img.src = frameUrl(set, last);
        img
          .decode()
          .then(() => {
            frames[last] = img;
            draw(1);
          })
          .catch(() => undefined);
        gsap.from([".hero-copy"], { autoAlpha: 0, duration: 0.6, ease: "power1.out" });
        // پرده‌ی SSR تا اینجا جلوی صفحه است؛ بی فریم‌هایی که منتظرشان بماند باید کنار برود
        preloader?.style.setProperty("display", "none");
        return () => {
          abort.abort();
          observer.disconnect();
        };
      }

      // میرایی نمایی مستقل از نرخ فریم: ~۹۰٪ فاصله در ۱۸۰ میلی‌ثانیه
      let frame = 0;
      let then = 0;
      const step = (now: number) => {
        const dt = Math.min(0.1, (now - then) / 1000);
        then = now;
        current += (target - current) * (1 - Math.exp(-dt * 12.5));
        if (Math.abs(target - current) < 1e-4) current = target;
        // دوربین با هر تغییر پیشرفت حرکت می‌کند، پس بین دو فریم هم دوباره کشیده می‌شود
        if (Math.abs(current - drawn) > 2e-5) draw(current);
        frame = current === target ? 0 : requestAnimationFrame(step);
      };
      const seek = (progress: number) => {
        target = Math.min(1, progress / FRAMES_END);
        if (frame || target === current) return;
        then = performance.now();
        frame = requestAnimationFrame(step);
      };
      const onProgress = () => seek(ScrollTrigger.getById(HERO_TRIGGER)?.progress ?? 0);
      root.addEventListener(HERO_PROGRESS, onProgress);

      // تا آماده شدن فریم‌ها اسکرول قفل است؛ پیش‌بارگذار جلوی صفحه است
      const html = document.documentElement;
      html.style.overflow = "hidden";
      const count = preloader?.querySelector<HTMLElement>("[data-count]");
      // پیشرفتِ نمایش‌داده: خودرو، جاده و شمارنده هر سه از آن خوانده می‌شوند و نرم به پیشرفت واقعی می‌رسند
      const shown = { p: 0 };
      const render = () => {
        preloader?.style.setProperty("--p", shown.p.toFixed(4));
        if (count) count.textContent = faPlainNumber(Math.round(shown.p * 100));
      };

      let opened = false;
      const open = () => {
        if (opened) return;
        opened = true;
        html.style.overflow = "";
        ScrollTrigger.refresh();
        current = target = Math.min(
          1,
          (ScrollTrigger.getById(HERO_TRIGGER)?.progress ?? 0) / FRAMES_END,
        );
        draw(current);
        const timeline = gsap.timeline({ defaults: { ease: EASE_OUT } });
        if (preloader) {
          // خودرو اول به مقصد می‌رسد (هر چه نزدیک‌تر، کوتاه‌تر)، بعد پرده بالا می‌رود
          timeline.to(shown, {
            p: 1,
            duration: 0.25 + 0.45 * (1 - shown.p),
            ease: "power2.out",
            overwrite: true,
            onUpdate: render,
          });
        }
        timeline.addLabel("lift");
        if (preloader) {
          timeline
            .to(preloader.children, { autoAlpha: 0, y: -12, duration: 0.45, stagger: 0.05 }, "lift")
            .to(
              preloader,
              { clipPath: "inset(0% 0% 100% 0%)", duration: 1, ease: "expo.inOut" },
              "lift+=0.2",
            )
            // گره مالِ React است؛ پنهان می‌شود، نه remove
            .set(preloader, { display: "none" });
        }
        timeline
          .from(".hero-canvas", { scale: 1.06, duration: 1.6 }, "lift+=0.25")
          .from(".hero-note", { autoAlpha: 0, y: 16, duration: 0.8 }, "lift+=0.7")
          .from(
            ".hero-line",
            { autoAlpha: 0, yPercent: 40, duration: 1, stagger: 0.09 },
            "lift+=0.72",
          )
          .from(".hero-lede", { autoAlpha: 0, y: 24, duration: 0.9 }, "lift+=0.9")
          .from(".hero-actions", { autoAlpha: 0, y: 24, duration: 0.9 }, "lift+=1");
      };
      const timeout = window.setTimeout(open, MAX_WAIT_MS);

      // فریم اول تنها می‌آید تا پوستر زیر پیش‌بارگذار آماده باشد، بعد بقیه به ترتیب
      loadFrames(
        set,
        frames,
        (done) => {
          if (done === 1) draw(current);
          // پس از باز شدن (مهلت تمام شده) خودرو به مقصد رسیده؛ فریم‌های دیررس عقبش نمی‌برند
          if (opened) return;
          // پیشرفت نسبت به همان تعداد فریمی است که برای باز شدن لازم است، نه همه‌ی فریم‌ها
          const p = Math.min(1, done / Math.min(READY_FRAMES, frames.length));
          preloader?.setAttribute("aria-valuenow", String(Math.round(p * 100)));
          gsap.to(shown, {
            p,
            duration: 0.6,
            ease: "power2.out",
            overwrite: true,
            onUpdate: render,
          });
          if (done >= READY_FRAMES) open();
        },
        abort.signal,
      ).then(() => {
        window.clearTimeout(timeout);
        if (!abort.signal.aborted) open();
      });

      return () => {
        abort.abort();
        window.clearTimeout(timeout);
        cancelAnimationFrame(frame);
        gsap.killTweensOf(shown);
        observer.disconnect();
        root.removeEventListener(HERO_PROGRESS, onProgress);
        html.style.overflow = "";
      };
    },
    { scope: ref },
  );
}
